import { isFunction } from "lodash"
import {
    isDeclarativeWizardQuestion,
    resolveDeclarativeWizard,
    type DeclarativeWizardAnswer,
    type DeclarativeWizardQuestion,
} from "./declarative-wizard"
import { createWizard, type Wizard, type WizardAnswer, type WizardComponentRenderer } from "./wizard"

/**
 * Convert a declarative wizard to a wizard that can be rendered.
 */
export function createWizardFromDeclaration<Result>(
    declaration: DeclarativeWizardQuestion<Result>,
    initialModel: Result,
): Wizard<Result> {
    const wizard = createWizard<Result>(initialModel)
    const resolved = resolveDeclarativeWizard(declaration)

    wizard.question(resolved.question, convertAnswers(wizard, resolved.options))

    return wizard
}

export function convertAnswer<Result>(
    wizard: Wizard<Result>,
    answer: DeclarativeWizardAnswer<Result>,
): WizardAnswer<Result> {
    return wizard.answer(answer.answer, () => {
        if (isDeclarativeWizardQuestion(answer.action)) {
            return convertQuestion(wizard, answer.action)
        }

        if (isFunction(answer.action)) {
            return wizard.component(answer.answer, answer.action as WizardComponentRenderer<Result>)
        }

        return
    }, answer.disabled ?? false)
}

export function convertAnswers<Result>(
    wizard: Wizard<Result>,
    answer: DeclarativeWizardAnswer<Result>[],
): WizardAnswer<Result>[] {
    const answers = answer.map((option): WizardAnswer<Result> => convertAnswer(wizard, option))

    return answers
}

export function convertQuestion<Result>(wizard: Wizard<Result>, question: DeclarativeWizardQuestion<Result>) {
    return wizard.createQuestion(question.question, convertAnswers(wizard, question.options))
}
